/**
 * Encapsulates drag-resize state and calculations for row/column grid panels.
 * Used by any component that renders a CSS grid with draggable resize handles.
 */
export class GridResizeHandler {
  private _resizingRow: number | null = null;
  private _resizingCol: number | null = null;
  private _resizeStartY = 0;
  private _resizeStartX = 0;
  private _resizeStartSizes: number[] = [];
  private _containerHeight = 0;
  private _containerWidth = 0;

  startRowResize(event: MouseEvent, rowIdx: number, sizes: number[], containerHeight: number): void {
    this._resizingRow = rowIdx;
    this._resizeStartY = event.clientY;
    this._resizeStartSizes = [...sizes];
    this._containerHeight = containerHeight;
    event.preventDefault();
  }

  startColResize(event: MouseEvent, colIdx: number, sizes: number[], containerWidth: number): void {
    this._resizingCol = colIdx;
    this._resizeStartX = event.clientX;
    this._resizeStartSizes = [...sizes];
    this._containerWidth = containerWidth;
    event.preventDefault();
  }

  /** Returns updated row sizes during a drag, or null if no row resize is active. */
  computeRowResize(event: MouseEvent, rowCount: number): number[] | null {
    if (this._resizingRow === null) return null;
    const deltaY = event.clientY - this._resizeStartY;
    const availableHeight = this._containerHeight - (rowCount - 1) * 3;
    const totalFr = this._resizeStartSizes.reduce((a, b) => a + b, 0);
    const deltaFr = (deltaY / availableHeight) * totalFr;
    const newSizes = [...this._resizeStartSizes];
    newSizes[this._resizingRow] = Math.max(0.05, this._resizeStartSizes[this._resizingRow] + deltaFr);
    newSizes[this._resizingRow + 1] = Math.max(0.05, this._resizeStartSizes[this._resizingRow + 1] - deltaFr);
    return newSizes;
  }

  /** Returns updated column sizes during a drag, or null if no column resize is active. */
  computeColResize(event: MouseEvent, colCount: number): number[] | null {
    if (this._resizingCol === null) return null;
    const deltaX = event.clientX - this._resizeStartX;
    const availableWidth = this._containerWidth - (colCount - 1) * 3;
    const totalFr = this._resizeStartSizes.reduce((a, b) => a + b, 0);
    const deltaFr = (deltaX / availableWidth) * totalFr;
    const newSizes = [...this._resizeStartSizes];
    newSizes[this._resizingCol] = Math.max(0.05, this._resizeStartSizes[this._resizingCol] + deltaFr);
    newSizes[this._resizingCol + 1] = Math.max(0.05, this._resizeStartSizes[this._resizingCol + 1] - deltaFr);
    return newSizes;
  }

  /** Clears row resize state. Returns true if a row resize was active. */
  endRowResize(): boolean {
    if (this._resizingRow !== null) {
      this._resizingRow = null;
      return true;
    }
    return false;
  }

  /** Clears column resize state. Returns true if a column resize was active. */
  endColResize(): boolean {
    if (this._resizingCol !== null) {
      this._resizingCol = null;
      return true;
    }
    return false;
  }
}
